import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { AdvancedReportShareDialogState, AdvancedReportShareDialogStateModel, InitAdvancedReportShareDialog, ReportShareFormData, SubmitShareDialogData } from "./advanced-report-share.dialog.state";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Snackbar } from "@core/material";
import { takeUntil } from "rxjs/operators";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { EmailValidators } from "@core/utils";
import { AdvancedReport, ReportAccess } from "@core/data/types/advanced-report";
import { AsyncValidators } from "@core/utils/validators/async-validators";
import { Status } from "@core/data";

export interface AdvancedReportShareDialogData {
    key: string;
    orgKey: string;
}

@Component({
    selector: 'pv-advanced-report-share-dialog',
    templateUrl: 'advanced-report-share.dialog.html',
    styleUrls:   ['advanced-report-share.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'pv-advanced-report-share-dialog'
    }
})
export class AdvancedReportShareDialog implements OnInit, OnDestroy {

    @Select(AdvancedReportShareDialogState)
    state$: Observable<AdvancedReportShareDialogStateModel>;

    emailValidator = EmailValidators.commaSeparatedEmailsWithSpaces();

    emailsFormControl: FormControl = new FormControl(null, {
        validators: [],
        asyncValidators: [AsyncValidators.debounce(this.emailValidator)],
        updateOn: 'change',
    });

    accessFormControl: FormControl = new FormControl(null, [Validators.required]);

    formGroup = new FormGroup({
        emails: this.emailsFormControl,
        access: this.accessFormControl,
    });

    accessOptions = [
        { value: ReportAccess.RESTRICTED, icon: 'lock', label: 'Restricted' },
        { value: ReportAccess.ANYONE, icon: 'globe', label: 'Anyone with the link' },
    ]

    isPending: boolean = false;

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _snackbar: Snackbar,
        private _dialogRef: MatDialogRef<AdvancedReportShareDialog>,
        private _changes: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) public data: AdvancedReportShareDialogData,
    ) {}

    ngOnInit() {
        this._store.dispatch(new InitAdvancedReportShareDialog(this.data.key, this.data.orgKey));

        this.state$.pipe(takeUntil(this._destroy$))
            .subscribe((state: AdvancedReportShareDialogStateModel) => {
                if (!state.data) return;

                if (state.status === Status.LOADING) {
                    this.isPending = true;
                    this._changes.detectChanges();

                    setTimeout(() => {
                        this.isPending = false;
                        this._changes.detectChanges();
                    }, 5000);
                }

                if (state.status === Status.COMPLETE){
                    this._snackbar.message('Report shared successfully');
                    this._dialogRef.close(true);
                }

                this.reset(state.data)
            })
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    reset(report: AdvancedReport) {
        let access = this.accessOptions.find(option => option.value === report.access);

        this.formGroup.reset({
            emails: null,
            access: access,
        });
    }

    copyLink(report: AdvancedReport) {
        const link = `${window.location.origin}/advanced-reports/preview/${report.shareKey}`;
        navigator.clipboard.writeText(link);
        this._snackbar.message('Report link copied to clipboard')
    }

    attempt() {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();

        if (!this.formGroup.valid) return this._snackbar.error("Invalid input. Check your input and try again");

        const form = this.formGroup.value;

        let emails = form.emails?.replace(/\s+/g, '')?.toLowerCase().split(',') || [];

        if (emails.length > 0) emails = emails.filter(email => email !== '');

        const data: ReportShareFormData = {
            emails: emails,
            access: form.access.value
        }

        this._store.dispatch(new SubmitShareDialogData(data));
    }

    cancel() {
        this._dialogRef.close(false);
    }
}